const DAY_INDEX = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const safeDate = (value) => {
  if (!value) return null;
  if (value?.year && value?.month && value?.date) {
    const date = new Date(value.year, value.month - 1, value.date);
    date.setHours(0, 0, 0, 0);
    return date;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const findEnrollmentBatch = (course, enrollment) =>
  (course?.batches || []).find(
    (item, index) =>
      String(item.batchId) === String(enrollment?.batchId) ||
      String(index) === String(enrollment?.batchId),
  ) || null;

const nextMatchingDate = (baseDate, activeDays) => {
  const probe = new Date(baseDate);
  for (let offset = 0; offset < 7; offset += 1) {
    probe.setDate(baseDate.getDate() + offset);
    if (activeDays.includes(probe.getDay())) {
      probe.setHours(0, 0, 0, 0);
      return new Date(probe);
    }
  }
  return new Date(baseDate);
};

const generateClassDates = ({ startDate, days, count }) => {
  if (!startDate || !count) return [];
  const activeDays = (days || [])
    .map((day) => DAY_INDEX[String(day).slice(0, 3).toUpperCase()])
    .filter((day) => Number.isInteger(day))
    .sort((a, b) => a - b);

  if (!activeDays.length) {
    return Array.from({ length: count }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index * 7);
      return date;
    });
  }

  const dates = [];
  let cursor = nextMatchingDate(startDate, activeDays);
  while (dates.length < count) {
    if (activeDays.includes(cursor.getDay())) {
      dates.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

export const getLessonScheduleDate = ({ course, enrollment, lesson, lessonIndex }) => {
  const explicit = safeDate(lesson?.date);
  if (explicit) return explicit;

  const batch = findEnrollmentBatch(course, enrollment);
  const startDate = safeDate(batch?.startDate || enrollment?.enrollmentDate);
  if (!startDate) return null;

  const generated = generateClassDates({
    startDate,
    days: batch?.schedule?.days || [],
    count: (course?.lessons || []).length,
  });

  return generated[lessonIndex] || null;
};

export const getLessonScheduleTime = ({ course, enrollment, lesson }) => {
  if (lesson?.time) return lesson.time;
  const batch = findEnrollmentBatch(course, enrollment);
  return batch?.schedule?.timeSlot || '';
};

export const buildScheduledLessons = ({ course, enrollment }) =>
  (course?.lessons || []).map((lesson, index) => {
    const scheduledDate = getLessonScheduleDate({
      course,
      enrollment,
      lesson,
      lessonIndex: index,
    });
    const now = new Date();
    return {
      ...lesson,
      lessonIndex: index,
      scheduledDate,
      scheduledDateIso: scheduledDate ? scheduledDate.toISOString() : '',
      scheduledTime: getLessonScheduleTime({ course, enrollment, lesson }),
      isAvailable: scheduledDate ? scheduledDate <= now : true,
      isUpcoming: scheduledDate ? scheduledDate > now : false,
    };
  });

export const buildStudentCalendarEvents = ({
  activeCourses = [],
  attendance = [],
  assignments = [],
}) => {
  const events = [];

  activeCourses.forEach((course) => {
    const scheduledLessons = buildScheduledLessons({
      course,
      enrollment: course.enrollment,
    });
    scheduledLessons.forEach((lesson) => {
      if (!lesson.scheduledDate) return;
      events.push({
        id: `lesson-${course.id}-${lesson.lessonIndex}`,
        type: lesson.isAvailable ? 'class' : 'upcoming_class',
        date: lesson.scheduledDate,
        dateKey: lesson.scheduledDate.toISOString().split('T')[0],
        title: lesson.title || `Lesson ${lesson.lessonIndex + 1}`,
        subtitle: course.name,
        courseId: course.id,
        time: lesson.scheduledTime || '',
      });
    });
  });

  attendance.forEach((record) => {
    const date = safeDate(record.markedAt || record.date);
    if (!date) return;
    events.push({
      id: `attendance-${record.id}`,
      type: record.status === 'present' ? 'attendance_present' : 'attendance_absent',
      date,
      dateKey: date.toISOString().split('T')[0],
      title: record.courseName || 'Attendance',
      subtitle: record.status === 'present' ? 'Present' : 'Absent',
      courseId: record.courseId,
    });
  });

  assignments.forEach((assignment) => {
    const date = safeDate(assignment.dueDate);
    if (!date) return;
    events.push({
      id: `assignment-${assignment.id || assignment.title}`,
      type: assignment.status === 'completed' ? 'assignment_done' : 'assignment_due',
      date,
      dateKey: date.toISOString().split('T')[0],
      title: assignment.title || 'Assignment',
      subtitle: assignment.courseName || 'Assignment due',
      courseId: assignment.courseId,
    });
  });

  return events.sort((a, b) => a.date - b.date);
};
